// 小組 Context 工具函數
/// @ts-check

import GroupContextTypes from '../types/groupContext.types.js';

/**
 * @typedef {import('../types/groupContext.types.js').GroupContext} GroupContext
 * @typedef {import('../types/groupContext.types.js').GroupMember} GroupMember
 * @typedef {import('../types/groupContext.types.js').GroupContextMode} GroupContextMode
 * @typedef {import('../types/groupContext.types.js').ConversationHistoryItem} ConversationHistoryItem
 * @typedef {import('../types/groupContext.types.js').HistoryFormatOptions} HistoryFormatOptions
 * @typedef {import('../types/groupContext.types.js').ContextValidationResult} ContextValidationResult
 */

/**
 * @typedef {Object} ContextBuildParams
 * @property {string} activityTitle - 活動標題
 * @property {string} activityId - 活動 ID
 * @property {string} groupId - 群組 ID
 * @property {string} currentUser - 當前用戶名稱
 * @property {string} [currentPath] - 當前路徑（用於自動檢測模式）
 */

/**
 * 統一構建小組 context - 兩種模式都使用相同邏輯
 * @param {Array<any>} graphNodes - 當前圖形節點（包含所有節點資料）
 * @param {Partial<ContextBuildParams>} params - 構建參數（從頁面層級傳入）
 * @param {GroupContextMode} mode - 模式標識 ('chat' | 'sticker' | 'auto')
 * @returns {GroupContext} 小組 context 對象
 */
export const buildUnifiedGroupContext = (graphNodes = [], params = {}, mode = 'auto') => {
  try {
    // 從參數中提取基本信息
    const {
      activityTitle = '未命名活動',
      activityId = '',
      groupId = '',
      currentUser = '匿名用戶',
      currentPath = ''
    } = params;
    
    // 自動檢測模式
    if (mode === 'auto' && currentPath) {
      if (currentPath.includes('chatroom')) {
        mode = 'chat';
      } else if (currentPath.includes('sticker') || currentPath.includes('research')) {
        mode = 'sticker';
      } else {
        mode = 'chat'; // 預設為聊天模式
      }
    }
    
    // 檢查是否為夥伴對話
    const isPartnerChat = activityTitle.includes('與') && activityTitle.includes('的對話');
    
    // 從節點獲取參與者信息
    const allAuthors = new Set([currentUser]); // 確保當前用戶在列表中
    
    // 處理圖形節點
    if (graphNodes && graphNodes.length > 0) {
      graphNodes.forEach(node => {
        if (node.data?.source?.author) {
          allAuthors.add(node.data.source.author);
        } else if (node.author) {
          // 處理直接的節點對象
          allAuthors.add(node.author);
        }
      });
    }
    
    // 構建成員列表
    const members = Array.from(allAuthors).map(author => {
      // 判斷角色（使用 MemberRole 類型）
      /** @type {'學生' | '夥伴' | '教師'} */
      let role = '學生';
      if (author.includes('夥伴') || author.includes('AI') || author.includes('助手')) {
        role = '夥伴';
      } else if (author.includes('老師') || author.includes('教師')) {
        role = '教師';
      }
      
      return {
        name: author,
        role: role
      };
    });
    
    // 根據模式調整 context
    const contextByMode = {
      chat: {
        description: '聊天室討論模式',
        interactionType: 'real-time-chat',
        expectation: '即時互動和回應'
      },
      sticker: {
        description: '想法貼紙研究模式', 
        interactionType: 'idea-creation',
        expectation: '深度思考和想法建構'
      }
    };
    
    const modeInfo = contextByMode[mode] || contextByMode.chat;
    
    return {
      // 基本信息
      activityId: activityId,
      activityTitle: activityTitle,
      activityDescription: modeInfo.description,
      
      // 小組信息
      groupId: groupId,
      groupName: `小組 ${groupId}`,
      memberCount: members.length,
      members: members,
      
      // 情境信息
      isPartnerChat: isPartnerChat,
      mode: mode,
      interactionType: modeInfo.interactionType,
      expectation: modeInfo.expectation,
      
      // 對話歷史（初始為空，由調用者使用 formatConversationHistory 填充）
      conversationHistory: [],
      
      // 元數據
      timestamp: new Date().toISOString(),
      contextVersion: '2.0-unified'
    };
  } catch (error) {
    // 降級方案 - 使用傳入的參數
    const { activityId = '', groupId = 'unknown', currentUser = '匿名' } = params || {};
    return {
      activityId: activityId,
      activityTitle: '未知活動',
      activityDescription: '默認活動',
      groupId: groupId,
      groupName: `小組 ${groupId}`,
      memberCount: 1,
      members: [{ 
        name: currentUser, 
        role: '學生' 
      }],
      isPartnerChat: false,
      mode: mode || 'chat',
      interactionType: 'unknown',
      expectation: '基本互動',
      timestamp: new Date().toISOString(),
      contextVersion: '2.0-unified-fallback',
      conversationHistory: [] // 降級方案中的空歷史
    };
  }
};

/**
 * 格式化對話歷史 - 基於當下問題智能選擇最相關的記錄
 * @param {Array<any>} graphNodes - 圖形節點（包含所有節點資料）
 * @param {string} currentQuestion - 當下問題（必須參數，用於相似度計算）
 * @param {string | number} groupId - 群組 ID（從頁面層級傳入）
 * @param {number} [limit=5] - 限制數量
 * @param {HistoryFormatOptions} [options={}] - 選項設定
 * @returns {ConversationHistoryItem[]} 格式化的對話歷史
 */
export const formatConversationHistory = (graphNodes, currentQuestion, groupId, limit = 5, options = {}) => {
  try {
    // 驗證必須參數
    if (!currentQuestion || typeof currentQuestion !== 'string' || currentQuestion.trim().length === 0) {
      console.error('formatConversationHistory 錯誤: currentQuestion 是必須參數且不能為空');
      throw new Error('currentQuestion 是必須參數，請提供有效的當下問題');
    }

    if (groupId == null) {
      console.warn('formatConversationHistory: 缺少 groupId，提前停止');
      return [];
    }
    const parsedGroupId = typeof groupId === 'string' ? parseInt(groupId) : groupId;
    const { strategy = 'hybrid', keywords = [] } = options;
    
    if (!graphNodes || !Array.isArray(graphNodes)) {
      return [];
    }
    
    // 精簡數據提取，統一處理不同格式的節點
    const extractNodeData = (node) => {
      try {
        // 處理不同的節點結構
        const source = node.data?.source;
        const directData = node;
        
        // 解析 groupId - 處理不同的 group 格式
        let extractedGroupId = null;
        const groupField = source?.group || directData.group;
        
        if (groupField) {
          if (typeof groupField === 'string') {
            // 處理 JSON 字符串格式，例如: '{"type":"partner_response","partnerId":"1","aiGenerated":true}'
            try {
              const groupObj = JSON.parse(groupField);
              extractedGroupId = groupObj.groupId || groupObj.partnerId;
            } catch (e) {
              // 如果不是 JSON，直接使用字符串值
              if (groupField === 'reply' || groupField === 'idea') {
                // 這些是特殊的 group 類型，使用當前 sessionStorage 的 groupId
                extractedGroupId = groupId;
              } else {
                extractedGroupId = groupField;
              }
            }
          } else if (typeof groupField === 'object') {
            extractedGroupId = groupField.groupId || groupField.partnerId;
          } else {
            extractedGroupId = groupField;
          }
        }
        
        // 如果還是沒有 groupId，使用傳入的 groupId
        if (!extractedGroupId) {
          extractedGroupId = parsedGroupId;
        }
        
        // 提取標題和內容 - 優先順序調整
        const extractedTitle = source?.title || directData.title || source?.label || directData.label || '';
        const extractedContent = source?.content || directData.content || '';
        
        // 處理作者信息 - 確保正確提取
        const extractedAuthor = source?.author || directData.author || 
                              (source?.label?.split('\n')?.[1]) || // 從 label 中提取作者（第二行）
                              '匿名';
        
        // 處理時間戳
        const extractedTimestamp = source?.createdAt || directData.createdAt || 
                                 directData.timestamp || new Date().toISOString();
        
        const result = {
          id: node.id || source?.id || directData.id,
          title: extractedTitle,
          content: extractedContent,
          author: extractedAuthor,
          createdAt: extractedTimestamp,
          groupId: extractedGroupId,
          nodeType: source?.group || directData.group || 'unknown'
        };
        
        return result;
        
      } catch (error) {
        console.error('extractNodeData 處理節點時出錯:', error);
        // 返回基本的降級版本
        return {
          id: node.id || 'unknown',
          title: node.data?.label || node.label || '',
          content: '',
          author: '未知',
          createdAt: new Date().toISOString(),
          groupId: parsedGroupId,
          nodeType: 'error'
        };
      }
    };

    // 計算文本相似度（基於詞彙重疊和語義相似性）
    const calculateTextSimilarity = (text1, text2) => {
      if (!text1 || !text2) return 0;
      
      const normalize = (text) => text.toLowerCase()
        .replace(/[^\w\s]/g, '') // 移除標點符號
        .split(/\s+/)
        .filter(word => word.length > 1); // 過濾短詞
      
      const words1 = normalize(text1);
      const words2 = normalize(text2);
      
      if (words1.length === 0 || words2.length === 0) return 0;
      
      // 計算詞彙重疊度
      const set1 = new Set(words1);
      const set2 = new Set(words2);
      const intersection = new Set([...set1].filter(x => set2.has(x)));
      const union = new Set([...set1, ...set2]);
      
      const jaccardSimilarity = intersection.size / union.size;
      
      // 計算餘弦相似度（簡化版本）
      const allWords = [...union];
      const vector1 = allWords.map(word => words1.filter(w => w === word).length);
      const vector2 = allWords.map(word => words2.filter(w => w === word).length);
      
      const dotProduct = vector1.reduce((sum, a, i) => sum + a * vector2[i], 0);
      const magnitude1 = Math.sqrt(vector1.reduce((sum, a) => sum + a * a, 0));
      const magnitude2 = Math.sqrt(vector2.reduce((sum, a) => sum + a * a, 0));
      
      const cosineSimilarity = magnitude1 && magnitude2 ? dotProduct / (magnitude1 * magnitude2) : 0;
      
      // 結合兩種相似度
      return (jaccardSimilarity * 0.4) + (cosineSimilarity * 0.6);
    };

    // 計算相關性分數（基於當下問題）
    const calculateRelevanceScore = (data) => {
      let score = 0;
      const text = `${data.title} ${data.content}`.toLowerCase();
      
      // 基於當下問題的相似度（權重最高）- currentQuestion 已驗證非空
      const questionSimilarity = calculateTextSimilarity(currentQuestion, text);
      score += questionSimilarity * 10; // 相似度權重最高
      
      // 直接關鍵字匹配加分
      const questionWords = currentQuestion.toLowerCase().split(/\s+/).filter(w => w.length > 1);
      questionWords.forEach(word => {
        if (text.includes(word)) {
          score += 2;
        }
      });
      
      // 基礎分數：內容長度和品質
      const contentLength = text.length;
      if (contentLength > 50) score += 1; // 降低基礎分數權重
      if (contentLength > 100) score += 0.5;
      
      // 節點類型權重
      const typeWeights = {
        'idea': 2,      // 想法節點權重
        'reply': 1.5,   // 回覆節點
        'question': 2.5, // 問題節點權重提高（與當下問題相關）
        'partner_response': 1, // 夥伴回應
        'unknown': 0.3
      };
      score += typeWeights[data.nodeType] || 0.3;
      
      // 作者多樣性加分
      const isStudentContent = !data.author.includes('夥伴') && !data.author.includes('AI');
      if (isStudentContent) score += 0.8; // 降低權重
      
      // 額外關鍵字匹配
      keywords.forEach(keyword => {
        if (text.includes(keyword.toLowerCase())) {
          score += 1.5; // 降低權重
        }
      });
      
      // 互動性加分（如果有回覆或被回覆）
      if (data.title.includes('回覆') || data.content.includes('回覆')) {
        score += 0.8;
      }
      
      // 時間新近性（7天內加分，但權重降低）
      const daysSinceCreated = (Date.now() - new Date(data.createdAt).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceCreated <= 7) {
        score += Math.max(0, 1 - (daysSinceCreated / 7) * 1); // 降低時間權重
      }
      
      return score;
    };
    
    // 過濾、排序並限制數量
    console.log('[formatConversationHistory] 開始過濾節點:', {
      totalNodes: graphNodes.length,
      groupId: parsedGroupId
    });
    
    const validNodes = graphNodes
      .map(extractNodeData)
      .filter((data, index) => {
        // 檢查是否有有效內容
        const hasValidTitle = data.title && data.title.trim().length > 0;
        const hasValidContent = data.content && data.content.trim().length > 0;
        const hasContent = hasValidTitle || hasValidContent;
        
        // 檢查群組匹配 - 更靈活的匹配邏輯
        let groupMatches = true; // 預設為 true
        
        if (parsedGroupId) {
          const nodeGroupId = data.groupId;
          
          // 檢查節點類型是否為 partner_response（JSON 字符串格式）
          const isPartnerResponse = typeof data.nodeType === 'string' && 
                                   (data.nodeType.includes('partner_response') || 
                                    data.nodeType.includes('aiGenerated'));
          
          // eslint-disable-next-line eqeqeq
          groupMatches = !nodeGroupId || // 節點沒有 groupId，視為屬於當前組（向後兼容）
                        nodeGroupId == parsedGroupId || // 使用 == 進行寬鬆比較
                        // 特殊情況：這些類型的節點屬於當前群組
                        ['reply', 'idea'].includes(data.nodeType) ||
                        // ChatRoom 模式：partner_response 也屬於當前對話
                        isPartnerResponse;
        }
        
        // 排除明顯的錯誤或無效節點
        const isValidNode = data.id && data.id !== 'unknown' && data.nodeType !== 'error';
        
        const shouldInclude = hasContent && groupMatches && isValidNode;
        
        // 调试前5个节点
        if (index < 5) {
          console.log(`[formatConversationHistory] 節點 ${index}:`, {
            id: data.id,
            title: data.title?.substring(0, 30),
            hasContent,
            groupMatches,
            isValidNode,
            shouldInclude,
            nodeGroupId: data.groupId,
            currentGroupId: parsedGroupId,
            nodeType: data.nodeType
          });
        }
        
        return shouldInclude;
      });
    
    console.log('[formatConversationHistory] 過濾結果:', {
      validNodesCount: validNodes.length,
      totalNodesCount: graphNodes.length
      });

    // 根據策略選擇節點
    let selectedNodes = [];
    
    if (strategy === 'recent') {
      // 僅按時間排序
      selectedNodes = validNodes
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, limit);
    } else if (strategy === 'relevance') {
      // 僅按相關性排序
      selectedNodes = validNodes
        .map(data => ({ ...data, relevanceScore: calculateRelevanceScore(data) }))
        .sort((a, b) => b.relevanceScore - a.relevanceScore)
        .slice(0, limit);
    } else {
      // hybrid 策略：結合相關性和時間
      const scoredNodes = validNodes.map(data => ({
        ...data,
        relevanceScore: calculateRelevanceScore(data)
      }));
      
      // 確保包含最近的幾個記錄
      const recentCount = Math.min(Math.ceil(limit * 0.3), 5);
      const relevantCount = limit - recentCount;
      
      const recentNodes = scoredNodes
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, recentCount);
      
      const relevantNodes = scoredNodes
        .filter(node => !recentNodes.some(recent => recent.id === node.id))
        .sort((a, b) => b.relevanceScore - a.relevanceScore)
        .slice(0, relevantCount);
      
      selectedNodes = [...recentNodes, ...relevantNodes]
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
    
    // 返回精簡但完整的格式
    const formattedHistory = selectedNodes.map(data => {
      // 清理和格式化標題
      const cleanTitle = data.title.replace(/<[^>]*>/g, '').trim();
      
      // 清理和格式化內容
      const cleanContent = (data.content || '').replace(/<[^>]*>/g, '').trim();
      
      return {
        id: data.id,
        title: cleanTitle,
        content: cleanContent,
        author: data.author,
        timestamp: data.createdAt,
        nodeType: data.nodeType,
        relevanceScore: data.relevanceScore || 0
      };
    });
    
    // 詳細的日誌輸出
    console.log('[formatConversationHistory] 完成:', {
      當下問題: currentQuestion?.substring(0, 30),
      策略: strategy,
      輸入節點數: graphNodes.length,
      有效節點數: validNodes.length,
      選中節點數: selectedNodes.length,
      最終輸出數: formattedHistory.length,
      參與者: [...new Set(formattedHistory.map(h => h.author))],
      平均相關性分數: formattedHistory.length > 0 ? 
        (formattedHistory.reduce((sum, h) => sum + h.relevanceScore, 0) / formattedHistory.length).toFixed(2) : 0
    });
    
    if (formattedHistory.length === 0 && graphNodes.length > 0) {
      console.warn('[formatConversationHistory] ⚠️ 有節點但返回空歷史！', {
        totalNodes: graphNodes.length,
        validNodesAfterFilter: validNodes.length,
        selectedNodesAfterStrategy: selectedNodes.length
      });
    }
    
    return formattedHistory;
    
  } catch (error) {
    console.error('格式化對話歷史失敗:', error);
    return [];
  }
};

/**
 * 自動提取當下問題（從圖形節點中找到最相關的問題或話題）
 * @param {Array<any>} [graphNodes=[]] - 圖形節點
 * @param {string} [fallback='當前討論話題'] - 當無法提取時的降級方案
 * @returns {string} 當下問題
 */
export const extractCurrentQuestion = (graphNodes = [], fallback = '當前討論話題') => {
  if (!graphNodes || !Array.isArray(graphNodes) || graphNodes.length === 0) {
    return fallback;
  }
  
  // 優先順序：問題節點 > 最新想法 > 最新內容
  const questionNodes = [];
  const ideaNodes = [];
  const allNodes = [];
  
  graphNodes.forEach(node => {
    const source = node.data?.source;
    const directData = node;
    
    const title = source?.title || directData.title || source?.label || directData.label || '';
    const content = source?.content || directData.content || '';
    const nodeType = source?.group || directData.group || 'unknown';
    const createdAt = source?.createdAt || directData.createdAt || directData.timestamp || new Date().toISOString();
    
    const nodeData = {
      title,
      content,
      nodeType,
      createdAt,
      hasContent: (title && title.trim().length > 0) || (content && content.trim().length > 0)
    };
    
    if (!nodeData.hasContent) return;
    
    allNodes.push(nodeData);
    
    // 分類節點
    if (nodeType === 'question' || title.includes('?') || title.includes('？') || 
        title.includes('如何') || title.includes('為什麼') || title.includes('怎麼')) {
      questionNodes.push(nodeData);
    } else if (nodeType === 'idea') {
      ideaNodes.push(nodeData);
    }
  });
  
  // 按時間排序
  const sortByTime = (nodes) => nodes.sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  
  // 選擇策略
  let selectedNode = null;
  
  if (questionNodes.length > 0) {
    // 優先選擇最新的問題節點
    selectedNode = sortByTime(questionNodes)[0];
  } else if (ideaNodes.length > 0) {
    // 其次選擇最新的想法節點
    selectedNode = sortByTime(ideaNodes)[0];
  } else if (allNodes.length > 0) {
    // 最後選擇最新的任何節點
    selectedNode = sortByTime(allNodes)[0];
  }
  
  if (!selectedNode) {
    return fallback;
  }
  
  // 提取問題文本
  let question = selectedNode.title || selectedNode.content || '';
  
  // 清理文本
  question = question.replace(/<[^>]*>/g, '').trim();
  
  // 如果太長，截取前100個字符
  if (question.length > 100) {
    question = question.substring(0, 100) + '...';
  }
  
  return question || fallback;
};

/**
 * 從當下問題中提取關鍵詞
 * @param {string} question - 當下問題
 * @returns {Array} 關鍵詞列表
 */
export const extractQuestionKeywords = (question) => {
  if (!question || typeof question !== 'string') return [];
  
  // 中文停用詞列表
  const stopWords = new Set([
    '的', '是', '在', '我', '你', '他', '她', '它', '們', '了', '過', '這', '那', '個', '一', '二', '三',
    '什麼', '為什麼', '怎麼', '如何', '哪裡', '何時', '誰', '嗎', '呢', '吧', '啊', '嗎', '？', '！',
    '也', '都', '還', '就', '又', '才', '更', '很', '最', '非常', '十分', '特別', '尤其'
  ]);
  
  // 提取關鍵詞
  const keywords = question
    .replace(/[^\w\s]/g, ' ') // 移除標點符號
    .split(/\s+/)
    .filter(word => 
      word.length > 1 && // 長度大於1
      !stopWords.has(word) && // 不是停用詞
      !/^\d+$/.test(word) // 不是純數字
    )
    .map(word => word.toLowerCase());
  
  return [...new Set(keywords)]; // 去重
};

/**
 * 合併 context 和 conversationHistory
 * @param {GroupContext} baseContext - 基礎 context（由 buildUnifiedGroupContext 生成）
 * @param {ConversationHistoryItem[]} conversationHistory - 對話歷史（由 formatConversationHistory 生成）
 * @returns {GroupContext} 完整的 context
 */
export const mergeContextWithHistory = (baseContext, conversationHistory) => {
  return {
    ...baseContext,
    conversationHistory: conversationHistory || []
  };
};

/**
 * 驗證 context 是否完整
 * @param {GroupContext} groupContext - 小組 context
 * @returns {ContextValidationResult} 驗證結果
 */
export const validateContext = (groupContext) => {
  const issues = [];
  
  if (!groupContext.activityTitle || groupContext.activityTitle === '未知活動') {
    issues.push('缺少活動標題');
  }
  
  if (!groupContext.members || groupContext.members.length === 0) {
    issues.push('缺少成員信息');
  }
  
  if (!groupContext.conversationHistory || groupContext.conversationHistory.length === 0) {
    issues.push('缺少對話歷史');
  }
  
  return {
    isValid: issues.length === 0,
    issues: issues,
    score: Math.max(0, 1 - (issues.length / 3)) // 簡單的完整性評分
  };
};
